<template>
<div class="template">
    <div class="flex jc-sb ai-c">
        <div class="flex-33">
            <button
                title="Filters"
                type="button"
                :disabled="is_loading"
                :class="{ 'primary': show_filters }"
                @click="show_filters = !show_filters">
                <i class="material-icons">filter_list</i>
                <span v-if="show_filters">Hide Filters</span>
                <span v-else>Show Filters</span>
            </button>
        </div>

        <div class="flex-33 ta-c">
            <vue-table-pagination
                ref="stats_table_pagination"
                @vuetable-pagination:change-page="onChangePage">
            </vue-table-pagination>
        </div>

        <div class="flex-33 ta-r">
            <button
                title="Export"
                class="mr-2"
                :disabled="is_loading"
                @click="onExport">
                <i class="material-icons">save_alt</i>
            </button>

            <button
                title="Refresh"
                type="button"
                :disabled="is_loading"
                @click="onLoadData">
                <i class="material-icons">refresh</i>
            </button>
        </div>
    </div>

    <div v-show="show_filters" class="flex ai-fe jc-sb mb-3">
        <div class="flex">
            <div style="margin-left: 9px; width: 134px;">
                <label for="filter-facility-number">Facility Number</label>
                <input id="filter-facility-number" type="text" v-model="filters.facility_number" />
            </div>

            <!-- Margin-left accounts for 16 + 1px border -->
            <!-- Width is column width - 16px -->
            <div style="margin-left: 17px; width: 134px;">
                <label for="filter-prime-site-id">Prime ID</label>
                <input id="filter-prime-site-id" type="text" v-model="filters.prime_site_id" />
            </div>

            <div style="margin-left: 17px; width: 284px;">
                <label for="filter-account-name">Facility Name</label>
                <input id="filter-account-name" type="text" v-model="filters.account_name" />
            </div>
        </div>
    </div>

    <div :class="['loading-wrapper', 'mt-4', { loading: is_loading }]">
        <vue-table ref="stats_table"
            api-url="/api/facility-stats"
            data-path="data.data"
            pagination-path="data.pagination"
            :http-options="http_options"
            :fields="columns"
            :per-page="100"
            table-height="475px"
            :append-params="api_params"
            detail-row-component="facility-stats-detail-row"
            @vuetable:loading="is_loading = true"
            @vuetable:loaded="is_loading = false"
            @vuetable:pagination-data="onPaginationData"
            @vuetable:row-clicked="toggleDetailRow"
            no-data-template="<span>No stats to show.</span>">
            <template slot="facility_number" slot-scope="props">
                <div class="flex jc-sb">
                    <span>{{ props.rowData.facility_number }}</span>

                    <span v-if="props.rowData.was_prime" class="prime" title="Prime Facility">
                        <strong>P</strong>
                    </span>
                </div>
            </template>

            <template slot="account_name" slot-scope="props">
                <span v-if="props.rowData.account_name === null || props.rowData.account_name.length < 35">
                    {{ props.rowData.account_name }}
                </span>

                <span v-else :title="props.rowData.account_name">
                    {{ props.rowData.account_name }}
                </span>
            </template>

            <template slot="selected_coverage_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.selected_coverage_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="confirmed_payment_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.confirmed_payment_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="provided_proof_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.provided_proof_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="opted_out_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.opted_out_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="moved_out_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.moved_out_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="bad_address_percentage" slot-scope="props">
                <span>{{ props.rowData.stats.bad_address_percentage }}</span>
                <span class="gray-message">%</span>
            </template>

            <template slot="percent_complete" slot-scope="props">
                <div class="flex ai-c jc-c pos-r">
                    <!-- <radial-progress-bar
                        :diameter="55"
                        :stroke-width="5"
                        :completed-steps="Math.floor(props.rowData.stats.percent_complete)"
                        :total-steps="100"
                        inner-stroke-color="#e8e8e8"
                        start-color="#83dbb3"
                        stop-color="#83dbb3" /> -->

                    <span class="percent-complete-text pos-a">
                        {{ Math.floor(props.rowData.stats.percent_complete) }}%
                    </span>
                </div>
            </template>

            <template slot="archive" slot-scope="props">
                <button
                    type="button"
                    :disabled="is_loading"
                    @click="startArchiving(props.rowData)">
                    <i class="material-icons">done</i>
                </button>
            </template>
        </vue-table>
    </div>

    <archive-facility-modal
        v-if="archiving_facility !== null"
        :facility="archiving_facility"
        @close="closeArchiveFacilityModal">
    </archive-facility-modal>
</div>
</template>

<script>
import http from "@/http"
import store from "@/store"
import { debounce } from "lodash"
import { facilityStatsColumns } from "@/utilities/columns"
// import RadialProgressBar from "vue-radial-progress"

export default {
    // components: {
    //     RadialProgressBar
    // },

    data () {
        return {
            archiving_facility: null,
            columns: facilityStatsColumns,
            http_options: {
                baseURL: http.defaults.baseURL,
                headers: { "X-Authorization": store.state.token.token }
            },
            filters: {
                account_name: "",
                facility_number: "",
                prime_site_id: ""
            },
            is_loading: false,
            show_filters: false
        }
    },

    computed: {
        api_params () {
            const params = {}

            for (const key in this.filters) {
                const value = this.filters[key]

                if (value.trim().length !== 0) {
                    params[key] = value.trim()
                }
            }

            return params
        }
    },

    watch: {
        filters: {
            deep: true,
            handler: debounce(function (newValue) {
                // NOTE: we cannot use an arrow function here
                this.onLoadData()
            }, 500)
        }
    },

    methods: {
        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.stats_table.changePage(page)
        },

        closeArchiveFacilityModal () {
            this.archiving_facility = null

            this.$refs.stats_table.reload()
        },

        async onExport () {
            this.is_loading = true

            try {
                const {
                    data: {
                        filename
                    }
                } = await http.get("/api/facility-stats/create-export")

                let downloadPath = `${http.defaults.baseURL}/api/facility-stats/download-export`
                downloadPath += `?filename=${filename}`

                window.open(downloadPath, "_blank")
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.is_loading = false
            }
        },

        onLoadData () {
            this.$refs.stats_table.reload()
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.stats_table_pagination.setPaginationData(paginationData)
        },

        async startArchiving (data) {
            this.archiving_facility = data
        },

        /**
         * Toggle the detail row for the clicked record.
         *
         * @param object data   Row data
         * @param Event  ev    Event object
         *
         * @return null
         */
        toggleDetailRow (data, ev) {
            for (const index in ev.path) {
                // let columns prevent clicking from happening
                if (ev.path[index].classList.contains("no-click")) {
                    return
                }

                // don't check past the table element
                if (ev.path[index].classList.contains("vuetable")) {
                    break
                }
            }

            this.$refs.stats_table.toggleDetailRow(data.id)
        }
    }
}
</script>

<style scoped>
.template /deep/ .floated.pagination {
    float: none;
}

.percent-complete-text {
    font-size: 0.9em;
}
</style>



// WEBPACK FOOTER //
// src/components/list-facilities-page/FacilityStatsTab.vue